import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Megaphone, Send } from 'lucide-react'
import {
  CAMPAIGN_AUDIENCES,
  SAMPLE_VALUES,
  renderNotification,
  sensitiveContent,
  type CampaignAudience,
} from '@concours/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Empty,
  Input,
  PageHeader,
  Select,
  Table,
  Textarea,
  useAuth,
  usePermissions,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { when, type Competition } from '../../lib/competition-types'
import type { Paginated } from '../../lib/rbac-types'
import { useSelection } from '../../lib/selection'
import {
  NOTIFICATIONS_KEY,
  useAudience,
  useCampaigns,
  type Campaign,
} from '../../lib/notifications'

const AUDIENCE_OPTIONS = Object.entries(CAMPAIGN_AUDIENCES).map(([value, label]) => ({
  value,
  label,
}))
const CAMPAIGN_STATUS = {
  queued: ['En attente', 'info'],
  running: ['En cours', 'info'],
  done: ['Envoyé', 'success'],
  failed: ['Échec', 'danger'],
} as const

/**
 * Envois groupés (`notifications.send`) : un message à un public d'un concours,
 * par le portail et par courriel (pas de SMS, ADR-010). 🔒 Contenu contrôlé :
 * ni note, ni rang, ni mot de passe.
 */
export function CampaignsPage() {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const competitions = useQuery({
    queryKey: ['admin', 'competitions', 'notifications'],
    queryFn: () =>
      session.api
        .get<Paginated<Competition>>('/admin/competitions', { query: { perPage: 100 } })
        .then((r) =>
          r.data.filter(
            (c) =>
              c.status !== 'draft' && can('notifications.send', { type: 'competition', id: c.id })
          )
        ),
  })
  const [chosen, setChosen] = useState('')
  const competitionId = chosen || competitions.data?.[0]?.id || ''
  const competition = competitions.data?.find((c) => c.id === competitionId)
  const [audience, setAudience] = useState<CampaignAudience>('submitted')
  const [stageId, setStageId] = useState('')
  const [channels, setChannels] = useState<('portal' | 'email')[]>(['portal', 'email'])
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [confirming, setConfirming] = useState(false)
  const stages = useSelection(competitionId, audience === 'stage')
  const recipients = useAudience(competitionId, audience, stageId)
  const campaigns = useCampaigns(competitionId)

  const local = {
    title: sensitiveContent(title)[0],
    body: sensitiveContent(body)[0],
  }
  const ready =
    competitionId &&
    title.trim().length >= 3 &&
    body.trim().length >= 10 &&
    channels.length > 0 &&
    !local.title &&
    !local.body &&
    (recipients.data ?? 0) > 0

  const send = useMutation({
    mutationFn: () =>
      session.api.post<Campaign>(`/admin/competitions/${competitionId}/notification-campaigns`, {
        audience,
        stageId: audience === 'stage' ? stageId : null,
        channels,
        title,
        body,
      }),
    onSuccess: async (c) => {
      toast.success(`Envoi groupé : ${c.total} destinataire(s)`)
      setConfirming(false)
      setTitle('')
      setBody('')
      setErrors({})
      await queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY })
    },
    onError: (error) => {
      setConfirming(false)
      setErrors(fieldErrors(error))
      toast.error(errorMessage(error))
    },
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (ready) setConfirming(true)
  }

  const columns: Column<Campaign>[] = [
    { key: 'date', header: 'Date', cell: (c) => when(c.createdAt) },
    {
      key: 'title',
      header: 'Message',
      cell: (c) => (
        <span className="stack" style={{ gap: 2 }}>
          <strong>{c.title}</strong>
          <span className="muted">
            {c.audienceLabel}
            {c.stage ? ` — ${c.stage}` : ''} ·{' '}
            {c.channels.map((x) => (x === 'portal' ? 'portail' : 'courriel')).join(' + ')}
          </span>
        </span>
      ),
    },
    { key: 'by', header: 'Par', cell: (c) => c.requestedBy ?? '—' },
    {
      key: 'status',
      header: 'Statut',
      cell: (c) => (
        <span className="stack" style={{ gap: 2 }}>
          <Badge variant={CAMPAIGN_STATUS[c.status][1]}>{CAMPAIGN_STATUS[c.status][0]}</Badge>
          <span className="muted">
            {c.done} / {c.total}
          </span>
        </span>
      ),
    },
  ]

  const sample = { ...SAMPLE_VALUES, concours: competition?.title ?? SAMPLE_VALUES.concours }

  return (
    <>
      <PageHeader
        eyebrow="Communication"
        title="Envois groupés"
        description="Un message à tous les candidats d’un public : dossiers non soumis, dossiers déposés, candidats recevables ou candidats d’une étape. Portail et courriel ; les SMS sont réservés aux alertes automatiques."
      />
      {competitions.isError && <Alert tone="danger">{errorMessage(competitions.error)}</Alert>}
      {competitions.data?.length === 0 && (
        <Alert tone="info">Aucun concours publié dans votre périmètre.</Alert>
      )}
      {competitionId && (
        <div className="template-layout">
          <Card>
            <CardHeader title="Nouveau message" />
            <CardBody>
              <form className="stack" onSubmit={submit} noValidate>
                <Select
                  label="Concours"
                  value={competitionId}
                  options={(competitions.data ?? []).map((c) => ({
                    value: c.id,
                    label: `${c.reference} — ${c.title}`,
                  }))}
                  onChange={(e) => {
                    setChosen(e.target.value)
                    setStageId('')
                  }}
                />
                <div className="grid-2">
                  <Select
                    label="Destinataires"
                    value={audience}
                    options={AUDIENCE_OPTIONS}
                    onChange={(e) => setAudience(e.target.value as CampaignAudience)}
                  />
                  {audience === 'stage' && (
                    <Select
                      label="Étape"
                      value={stageId}
                      placeholder="Choisir une étape"
                      options={(stages.data?.stages ?? []).map((s) => ({
                        value: s.id,
                        label: `${s.position}. ${s.label}`,
                      }))}
                      error={errors.stageId}
                      onChange={(e) => setStageId(e.target.value)}
                    />
                  )}
                </div>
                <fieldset className="stack" style={{ gap: 'var(--space-2)' }}>
                  <legend className="field-label">Canaux</legend>
                  {(['portal', 'email'] as const).map((c) => (
                    <label key={c} className="check-row">
                      <input
                        type="checkbox"
                        checked={channels.includes(c)}
                        onChange={(e) =>
                          setChannels(
                            e.target.checked ? [...channels, c] : channels.filter((x) => x !== c)
                          )
                        }
                      />
                      <span>{c === 'portal' ? 'Centre de notifications' : 'Courriel'}</span>
                    </label>
                  ))}
                </fieldset>
                <Input
                  label="Titre (objet du courriel)"
                  required
                  value={title}
                  error={local.title ?? errors.title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <Textarea
                  label="Message"
                  required
                  rows={7}
                  value={body}
                  error={local.body ?? errors.body}
                  hint="Variables : {prenom}, {concours}, {matricule}, {lien}. Pas de note, de rang ni de donnée personnelle."
                  onChange={(e) => setBody(e.target.value)}
                />
                <div
                  className="row"
                  style={{
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 'var(--space-3)',
                  }}
                >
                  <span className="muted" aria-live="polite">
                    {recipients.isFetching
                      ? 'Calcul des destinataires…'
                      : `${recipients.data ?? 0} destinataire(s)`}
                  </span>
                  <Button variant="primary" type="submit" icon={<Send />} disabled={!ready}>
                    Envoyer
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
          <aside
            className="stack template-preview"
            aria-label="Aperçu"
            style={{ gap: 'var(--space-4)' }}
          >
            <h2 className="eyebrow">Aperçu</h2>
            <div className="preview-mail">
              <div className="preview-mail-subject">
                {renderNotification(title, sample) || 'Titre'}
              </div>
              <div className="preview-mail-body">
                {renderNotification(body, sample) || 'Message'}
              </div>
            </div>
          </aside>
        </div>
      )}
      {competitionId && (
        <Card>
          <CardHeader title="Envois précédents" />
          <Table
            columns={columns}
            rows={campaigns.data?.data ?? []}
            rowKey={(c) => c.id}
            loading={campaigns.isPending}
            caption="Envois groupés du concours"
            empty={
              <Empty
                icon={<Megaphone aria-hidden="true" />}
                title="Aucun envoi groupé"
                description="Les messages envoyés pour ce concours apparaîtront ici."
              />
            }
          />
        </Card>
      )}
      <ConfirmModal
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => send.mutate()}
        title="Envoyer ce message ?"
        message={`${recipients.data ?? 0} candidat(s) le recevront${channels.includes('email') ? ' (portail et courriel)' : ' dans le centre de notifications'}. Un envoi ne peut pas être annulé.`}
        confirmLabel="Envoyer"
      />
    </>
  )
}
